/**
 * Pure helpers the clinical forms (medications, treatments) share with the
 * Warehouse. This module imports no hook: the medication form schema reaches
 * it, and a hook module would pull the organization context (and Firebase)
 * into every test of that form (notes-2 F-2).
 */
import { translateKey } from 'src/locales/translate';
import { formatDateOnly, todayDateOnly } from './dates';
import { warehouseErrorCode, warehouseErrorMessage } from './errors';
import { formatMilli, formatUnits, parseQuantityInput } from './quantity';
import { formatProductStock, unitLabelsFor } from './stockDisplay';

export {
  formatDateOnly,
  formatMilli,
  formatProductStock,
  formatUnits,
  todayDateOnly,
  unitLabelsFor,
};

export type StockMeasure = 'PACKAGE' | 'UNIT';
export type StockHandover = 'ADMINISTERED' | 'HANDED_OVER';
export type ClinicalProductKind = 'DRUG' | 'NON_DRUG';

/**
 * Key `['warehouse-products', organizationId, 'clinical-picker', kind,
 * search]`: the shape `useWarehouseQuery` builds (master S3, organization
 * right after the prefix). Written out here so this module stays hook-free.
 */
export function clinicalPickerKey(
  organizationId: string | undefined,
  kind: ClinicalProductKind,
  search: string,
): readonly unknown[] {
  return ['warehouse-products', organizationId, 'clinical-picker', kind, search];
}

export interface StockWarningView {
  productId: string;
  productName: string;
  shortfallMilli: number;
}

export interface StockBatchView {
  id: string;
  batchNumber: string;
  /** `YYYY-MM-DD` */
  expiryDate: string;
  onHandMilli: number;
}

const MILLI_PER_UNIT = 1000;

/**
 * The quantity a clinical field holds, as a positive number, or `null`.
 * A text field hands its value over as the typed string (`RHFTextField`),
 * so a watched value is read like Phase 1 reads a typed quantity.
 */
export function parseStockQuantity(quantity: number | string): number | null {
  const value =
    typeof quantity === 'number' ? quantity : parseQuantityInput(quantity);
  return value !== null && Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * What a clinical line will take, in thousandths of a Unit; the same
 * conversion as the backend's `toQuantityMilli`.
 */
export function requestedMilli(
  quantity: number | string,
  measure: StockMeasure,
  unitsPerPackage: number,
): number {
  const value = parseStockQuantity(quantity);
  if (value === null) return 0;
  const units = measure === 'PACKAGE' ? value * unitsPerPackage : value;
  return Math.round(units * MILLI_PER_UNIT);
}

/** A stored quantity is shown back in Units: exact whatever the pack size. */
export function toStockInput(quantityMilli: number): {
  quantity: number;
  measure: StockMeasure;
} {
  return { quantity: quantityMilli / MILLI_PER_UNIT, measure: 'UNIT' };
}

/**
 * The Batches FEFO would take from, in the order it would take them (the
 * backend's `warehouse-allocation.ts`): in stock, not expired on `today`
 * (a Warsaw `YYYY-MM-DD`), soonest expiry first.
 */
export function usableBatches<T extends StockBatchView>(
  batches: T[],
  today: string,
): T[] {
  return batches
    .filter((batch) => batch.onHandMilli > 0 && batch.expiryDate >= today)
    .sort(
      (a, b) =>
        a.expiryDate.localeCompare(b.expiryDate) || a.id.localeCompare(b.id),
    );
}

function isWarning(value: unknown): value is StockWarningView {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Record<keyof StockWarningView, unknown>>;
  return (
    typeof candidate.productId === 'string' &&
    typeof candidate.productName === 'string' &&
    typeof candidate.shortfallMilli === 'number'
  );
}

function warningsOf(value: unknown): StockWarningView[] {
  if (typeof value !== 'object' || value === null) return [];
  const warnings = (value as { stockWarnings?: unknown }).stockWarnings;
  return Array.isArray(warnings) ? warnings.filter(isWarning) : [];
}

/** The body of an Axios response; mutations hand the whole response over. */
export function responsePayload(response: unknown): unknown {
  return typeof response === 'object' &&
    response !== null &&
    'data' in response
    ? (response as { data: unknown }).data
    : undefined;
}

/**
 * Shortages a write left. List endpoints put them on the written entry,
 * visit endpoints on the response object.
 */
export function extractStockWarnings(data: unknown): StockWarningView[] {
  return Array.isArray(data) ? data.flatMap(warningsOf) : warningsOf(data);
}

const CLINICAL_WRITE_REFUSAL = 'ClinicalWriteRefusal';

/**
 * A clinical write the screen refuses before sending anything; `message` is
 * the Polish reason the user reads (see `stockErrorMessage`).
 */
export function clinicalWriteRefusal(message: string): Error {
  const error = new Error(message);
  error.name = CLINICAL_WRITE_REFUSAL;
  return error;
}

/**
 * A Warehouse domain refusal in Polish (Phase 1's `warehouseErrorMessage`),
 * or a `clinicalWriteRefusal`'s reason, or `null` so the caller keeps its own
 * error copy for anything else.
 */
export function stockErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.name === CLINICAL_WRITE_REFUSAL) {
    return error.message;
  }
  return warehouseErrorCode(error)
    ? warehouseErrorMessage(error, translateKey)
    : null;
}
